import React, { useState } from 'react'
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { LayoutControls } from '../api/useDashboardLayout'
import { useCurrentRole } from '../currentRole'
import { advancedColors } from '../theme'
import { LayoutDefinition } from './layout'
import { PANELS, PanelOfferScope, alternativesFor, isPanelOfferable } from './registry'
import { PANEL_SPANS, SPAN_LABELS } from './spans'

type Props = {
  layout: LayoutDefinition
  index: number
  controls: LayoutControls
  scope: PanelOfferScope
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
    {children}
  </Typography>
)

const iconSx = { fontSize: 18, mr: 1.5, color: advancedColors.ink }

const PanelMenu = ({ layout, index, controls, scope }: Props) => {
  const [role] = useCurrentRole()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const entry = layout.panels[index]
  const panel = PANELS[entry.panel]
  const alternatives = alternativesFor(entry.panel).filter((candidate) => isPanelOfferable(candidate.id, role, scope))
  const close = () => setAnchor(null)

  const act = (change: () => void) => () => {
    change()
    close()
  }

  return (
    <>
      <Tooltip title={`Change what is shown here — ${panel.title}`}>
        <IconButton
          size="small"
          aria-label={`Change what is shown here — ${panel.title}`}
          aria-haspopup="true"
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ color: advancedColors.inkMuted }}
        >
          <ExpandMoreRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 248 } }}
      >
        {alternatives.length > 1 && <SectionLabel>Show here</SectionLabel>}
        {alternatives.length > 1 &&
          alternatives.map((candidate) => (
            <MenuItem
              key={candidate.id}
              selected={candidate.id === entry.panel}
              onClick={act(() => controls.show(index, candidate.id))}
            >
              {candidate.id === entry.panel ? (
                <CheckRoundedIcon sx={{ ...iconSx, color: advancedColors.green }} />
              ) : (
                <Box sx={{ width: 18, mr: 1.5 }} />
              )}
              {candidate.title}
            </MenuItem>
          ))}
        {alternatives.length > 1 && <Divider />}

        <SectionLabel>Width</SectionLabel>
        <Box sx={{ px: 2, pb: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={entry.span}
            onChange={(_event, span) => span && controls.resize(index, span)}
            aria-label="Panel width"
          >
            {PANEL_SPANS.map((span) => (
              <ToggleButton key={span} value={span} sx={{ px: 1 }}>
                {SPAN_LABELS[span]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider />

        <MenuItem onClick={act(() => controls.remove(index))}>
          <CloseRoundedIcon sx={iconSx} />
          Remove from dashboard
        </MenuItem>
      </Menu>
    </>
  )
}

export default PanelMenu
